import { Stack, Textarea, Text } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import dayjs from 'dayjs';
import { Icon } from '@/components';
import { Calendar, ChevronDown } from 'react-feather';
import { WorkLogStatus } from '@/generated/types/navigator/types.gen';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import classes from './WorklogFormFields.module.css';

const NOTE_CHAR_LIMIT = 250;

interface WorklogFormFieldsProps {
  status: WorkLogStatus;
  dateValue: Date;
  onDateChange: (date: Date | null) => void;
  notesValue: string;
  onNotesChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  notesError?: React.ReactNode;
}

export const WorklogFormFields = ({
  status,
  dateValue,
  onDateChange,
  notesValue,
  onNotesChange,
  notesError,
}: WorklogFormFieldsProps) => {
  const intl = useClientTerminology();

  const dateLabel =
    status === WorkLogStatus.COMPLETED
      ? intl.formatMessage({ id: 'worklog.dateOfCompletion' })
      : intl.formatMessage({ id: 'worklog.dateOfInspection' });

  const notesLength = notesValue?.length || 0;
  const showCharCount = notesLength > 0 && notesLength <= NOTE_CHAR_LIMIT;

  return (
    <Stack gap="md" mt="md">
      <DateTimePicker
        data-testid="worklog-date-input"
        locale={navigator.language}
        valueFormat="L LT"
        maxDate={new Date()}
        value={dateValue}
        onChange={(value) => onDateChange(value ? dayjs(value).toDate() : null)}
        classNames={{
          input: classes.dateInput,
          label: classes.fieldLabel,
          day: classes.calendarDay,
          root: classes.datePickerRoot,
        }}
        rightSection={<Icon Component={ChevronDown} size={16} />}
        leftSection={<Icon Component={Calendar} size={16} />}
        label={dateLabel}
        placeholder={intl.formatMessage({ id: 'worklog.datePlaceholder' })}
      />
      <Stack gap={4}>
        <Textarea
          data-testid="worklog-notes-input"
          value={notesValue}
          onChange={onNotesChange}
          error={notesError}
          placeholder={intl.formatMessage({ id: 'worklog.notesPlaceholder' })}
          label={intl.formatMessage({ id: 'worklog.notesLabel' })}
          classNames={{
            input: classes.textareaInput,
            label: classes.fieldLabel,
          }}
          h={100}
        />
        {showCharCount && (
          <Text
            size="xs"
            c="dimmed"
            ta="right"
            data-testid="worklog-char-count"
          >
            {notesLength}/{NOTE_CHAR_LIMIT}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};
